import PropTypes from 'prop-types';
import React from 'react';
import { Form, Input } from 'antd';
import { withTranslation } from 'react-i18next';
import ClabeValidator from 'clabe-validator';

import NumericInput from '../../../components/NumericInput/NumericInput';
import MaskInput from '../../../components/MaskInput/MaskInput';
import money from '../../../helpers/money';
import { validateRfcOrCurp } from '../../../helpers/validators';

const expectedBankAccountFieldLength = 18;
const rfcOrCurpMaxLength = 18;

const SpeiExternalTransaction = ({ getFieldDecorator, t, currency, balance }) => (
  <Form layout="vertical">
    <Form.Item label={t('transaction.external.name')}>
      {/* TODO: Add suppressWarning, when this issue is resolved: https://github.com/react-component/form/issues/313 */}
      {getFieldDecorator('ext_name', {
        rules: [
          {
            required: true,
            message: t('transaction.external.required.name'),
          },
        ],
      })(<Input name="ext_name" />)}
    </Form.Item>

    <Form.Item label={t('transaction.external.bank_account')}>
      {getFieldDecorator('ext_bank_account', {
        rules: [
          {
            required: true,
            message: t('transaction.external.required.bank_account'),
          },
          { min: expectedBankAccountFieldLength, message: t('transaction.external.min.bank_account') },
          { max: expectedBankAccountFieldLength, message: t('transaction.external.max.bank_account') },
          {
            validator: (options, value, callback) => {
              if (value.length !== expectedBankAccountFieldLength) { callback([]); return; }
              const { ok } = ClabeValidator.validate(value);
              const invalidBankAccount = [t('transaction.external.invalid.bank_account')];
              if (!ok) { callback(invalidBankAccount); }
              callback([]);
            },
          },
        ],
      })(<MaskInput pattern="##################" name="ext_bank_account" />)}
    </Form.Item>

    <Form.Item label={t('transaction.external.rfc_or_curp')}>
      {getFieldDecorator('ext_rfc_or_curp', {
        rules: [
          {
            required: true,
            message: t('transaction.external.required.rfc_or_curp'),
          },
          { max: rfcOrCurpMaxLength, message: t('transaction.external.max.rfc_or_curp') },
          {
            validator: (options, value, callback) => {
              if (!value || (value.length > rfcOrCurpMaxLength)) { callback([]); return; }
              const validated = validateRfcOrCurp(value);
              const invalidRfcOrCurp = [t('transaction.external.invalid.rfc_or_curp')];
              if (!validated) { callback(invalidRfcOrCurp); return; }
              callback([]);
            },
          },
        ],
      })(<Input name="ext_rfc_or_curp" />)}
    </Form.Item>

    <Form.Item label={t('transaction.external.reference')}>
      {getFieldDecorator('ext_desc', {
        rules: [],
      })(<Input name="ext_desc" />)}
    </Form.Item>

    <Form.Item label={t('transaction.external.amount')}>
      {getFieldDecorator('ext_amount', {
        rules: [
          {
            required: true,
            message: t('transaction.external.required.amount'),
          },
          { min: 1, message: t('transaction.external.min.amount') },
          {
            validator: (options, value, callback) => {
              const minAmountError = [t('transaction.external.min.amount')];
              const maxAmountError = [t('transaction.external.max.amount')];
              const notNumericAmountError = [t('transaction.external.invalid.numeric_amount')];
              let numericValue;
              try {
                numericValue = money.toInt(`${value}`, currency, false);
              } catch (error) {
                console.error('ERROR: money.toInt (SpeiExternalTransaction\'s amount validator): ', error); // eslint-disable-line no-console
              }
              let errors;
              switch (true) {
                case (Number.isNaN(numericValue) && !!value):
                  errors = notNumericAmountError;
                  break;
                case (numericValue === 0):
                  errors = minAmountError;
                  break;
                case (numericValue > balance):
                  errors = maxAmountError;
                  break;
                default:
                  break;
              }
              callback(errors);
            },
          },
        ],
      })(
        <NumericInput
          showtooltip
          showprefix
          tooltiptitle={t('transaction.external.amount')}
          currency={currency}
          name="ext_amount"
        />,
      )}
    </Form.Item>
  </Form>
);

SpeiExternalTransaction.propTypes = {
  balance: PropTypes.number,
  currency: PropTypes.string,
  getFieldDecorator: PropTypes.func,
  t: PropTypes.func,
};
const TranslatedSpeiExternalTransaction = withTranslation()(SpeiExternalTransaction);
export { TranslatedSpeiExternalTransaction as SpeiExternalTransaction };
